import { getZrmLeaseInfo, getZrmListingInfo, getZrmUserInfo } from '@zg-rentals/clickstream';
import { trackLeaseListingUserEvent, trackListingUserEvent } from '../../../client/analytics';
import type { ListingUserEventArgs } from '../../../client/analytics';

// Irene Landing Page
// https://dataportal.zillowgroup.net/event-registry/2553
const emitStartCollectingPaymentsEvent = ({ currentProperty, user }: ListingUserEventArgs) =>
  trackListingUserEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '2553',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'irene_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'start_collecting_payments',
    },
    semantic: {
      semantic_event_nm: 'start_collecting_payments',
      topic_tag_txt: ['zrm_payments'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/2554
const emitLearnMoreLinkEvent = ({ currentProperty, user }: ListingUserEventArgs) =>
  trackListingUserEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '2554',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'irene_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'link_to_learn_more',
    },
    semantic: {
      semantic_event_nm: 'learn_more',
      topic_tag_txt: ['zrm_payments'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// Irene Checklist Page
// https://dataportal.zillowgroup.net/event-registry/2555
const emitContinueFromChecklistPageEvent = ({ currentProperty, user }: ListingUserEventArgs) =>
  trackListingUserEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '2555',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'irene_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'checklist_page',
      trigger_source_nm: 'button_ok_got_it',
    },
    semantic: {
      semantic_event_nm: 'continue_payments_onboarding',
      topic_tag_txt: ['zrm_payments'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// Irene Lease Selector Page
// https://dataportal.zillowgroup.net/event-registry/2556
const emitContinueWithLeaseSelectedEvent = ({ currentProperty, user }: ListingUserEventArgs) =>
  trackLeaseListingUserEvent({
    envelope: {
      event_template_id: '177',
      event_template_version_id: '1',
      event_type_id: '2556',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'irene_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'lease_selector',
      trigger_source_nm: 'continue_with_lease_selected',
    },
    semantic: {
      semantic_event_nm: 'continue_payments_onboarding',
      topic_tag_txt: ['zrm_payments'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
    ...getZrmLeaseInfo({}),
  });

// https://dataportal.zillowgroup.net/event-registry/2559
const emitContinueFromLeaseSelectorToAddPaymentManuallyEvent = ({ currentProperty, user }: ListingUserEventArgs) =>
  trackLeaseListingUserEvent({
    envelope: {
      event_template_id: '177',
      event_template_version_id: '1',
      event_type_id: '2559',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'irene_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'lease_selector',
      trigger_source_nm: 'setup_manually',
    },
    semantic: {
      semantic_event_nm: 'continue_payments_onboarding',
      topic_tag_txt: ['zrm_payments'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
    ...getZrmLeaseInfo({}),
  });

// Irene Rent Amount Page
// https://dataportal.zillowgroup.net/event-registry/2561
const emitContinueFromRentAmountPageEvent = ({ currentProperty, user }: ListingUserEventArgs) =>
  trackLeaseListingUserEvent({
    envelope: {
      event_template_id: '177',
      event_template_version_id: '1',
      event_type_id: '2561',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'irene_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'rent_amount',
      trigger_source_nm: 'continue',
    },
    semantic: {
      semantic_event_nm: 'continue_payments_onboarding',
      topic_tag_txt: ['zrm_payments'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
    ...getZrmLeaseInfo({}),
  });

// Irene Due Date Page
// https://dataportal.zillowgroup.net/event-registry/2562
const emitContinueFromDueDatePageEvent = ({ currentProperty, user }: ListingUserEventArgs) =>
  trackLeaseListingUserEvent({
    envelope: {
      event_template_id: '177',
      event_template_version_id: '1',
      event_type_id: '2562',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'irene_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'due_day',
      trigger_source_nm: 'continue',
    },
    semantic: {
      semantic_event_nm: 'continue_payments_onboarding',
      topic_tag_txt: ['zrm_payments'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
    ...getZrmLeaseInfo({}),
  });

// Irene Term Length Page
// https://dataportal.zillowgroup.net/event-registry/2563
const emitContinueFromTermLengthPageEvent = ({ currentProperty, user }: ListingUserEventArgs) =>
  trackLeaseListingUserEvent({
    envelope: {
      event_template_id: '177',
      event_template_version_id: '1',
      event_type_id: '2563',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'irene_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'term_length',
      trigger_source_nm: 'continue',
    },
    semantic: {
      semantic_event_nm: 'continue_payments_onboarding',
      topic_tag_txt: ['zrm_payments'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
    ...getZrmLeaseInfo({}),
  });

// Irene Move In Costs Page
// https://dataportal.zillowgroup.net/event-registry/2565
const emitContinueFromSecurityDepositEvent = ({ currentProperty, user }: ListingUserEventArgs) =>
  trackLeaseListingUserEvent({
    envelope: {
      event_template_id: '177',
      event_template_version_id: '1',
      event_type_id: '2565',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'irene_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'move_in_costs | security deposit',
      trigger_source_nm: 'yes_and_continue',
    },
    semantic: {
      semantic_event_nm: 'continue_payments_onboarding',
      topic_tag_txt: ['zrm_payments'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
    ...getZrmLeaseInfo({}),
  });

// https://dataportal.zillowgroup.net/event-registry/2566
const emitSkipSecurityDepositEvent = ({ currentProperty, user }: ListingUserEventArgs) =>
  trackLeaseListingUserEvent({
    envelope: {
      event_template_id: '177',
      event_template_version_id: '1',
      event_type_id: '2566',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'irene_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'move_in_costs | security deposit',
      trigger_source_nm: 'no_and_continue',
    },
    semantic: {
      semantic_event_nm: 'continue_payments_onboarding',
      topic_tag_txt: ['zrm_payments'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
    ...getZrmLeaseInfo({}),
  });

// https://dataportal.zillowgroup.net/event-registry/2568
const emitContinueFromProratedRentEvent = ({ currentProperty, user }: ListingUserEventArgs) =>
  trackLeaseListingUserEvent({
    envelope: {
      event_template_id: '177',
      event_template_version_id: '1',
      event_type_id: '2568',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'irene_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'move_in_costs | prorated_rent',
      trigger_source_nm: 'yes_and_continue',
    },
    semantic: {
      semantic_event_nm: 'continue_payments_onboarding',
      topic_tag_txt: ['zrm_payments'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
    ...getZrmLeaseInfo({}),
  });

// https://dataportal.zillowgroup.net/event-registry/2567
const emitSkipProratedRentEvent = ({ currentProperty, user }: ListingUserEventArgs) =>
  trackLeaseListingUserEvent({
    envelope: {
      event_template_id: '177',
      event_template_version_id: '1',
      event_type_id: '2567',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'irene_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'move_in_costs | prorated_rent',
      trigger_source_nm: 'no_and_continue',
    },
    semantic: {
      semantic_event_nm: 'continue_payments_onboarding',
      topic_tag_txt: ['zrm_payments'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
    ...getZrmLeaseInfo({}),
  });

// https://dataportal.zillowgroup.net/event-registry/2569
const emitContinueFromOtherMICEvent = ({ currentProperty, user }: ListingUserEventArgs) =>
  trackLeaseListingUserEvent({
    envelope: {
      event_template_id: '177',
      event_template_version_id: '1',
      event_type_id: '2569',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'irene_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'move_in_costs | other_move_in_costs',
      trigger_source_nm: 'yes_and_continue',
    },
    semantic: {
      semantic_event_nm: 'continue_payments_onboarding',
      topic_tag_txt: ['zrm_payments'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
    ...getZrmLeaseInfo({}),
  });

// https://dataportal.zillowgroup.net/event-registry/2570
const emitSkipOtherMICEvent = ({ currentProperty, user }: ListingUserEventArgs) =>
  trackLeaseListingUserEvent({
    envelope: {
      event_template_id: '177',
      event_template_version_id: '1',
      event_type_id: '2570',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'irene_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'move_in_costs | other_move_in_costs',
      trigger_source_nm: 'no_and_continue',
    },
    semantic: {
      semantic_event_nm: 'continue_payments_onboarding',
      topic_tag_txt: ['zrm_payments'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
    ...getZrmLeaseInfo({}),
  });

// https://dataportal.zillowgroup.net/event-registry/2571
const emitContinueFromMICDueDateEvent = ({ currentProperty, user }: ListingUserEventArgs) =>
  trackLeaseListingUserEvent({
    envelope: {
      event_template_id: '177',
      event_template_version_id: '1',
      event_type_id: '2571',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'irene_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'move_in_date',
      trigger_source_nm: 'continue',
    },
    semantic: {
      semantic_event_nm: 'continue_payments_onboarding',
      topic_tag_txt: ['zrm_payments'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
    ...getZrmLeaseInfo({}),
  });

// Irene Invite Tenant Page
// https://dataportal.zillowgroup.net/event-registry/2564
const emitContinueFromInviteTenantPageEvent = ({ currentProperty, user }: ListingUserEventArgs) =>
  trackLeaseListingUserEvent({
    envelope: {
      event_template_id: '177',
      event_template_version_id: '1',
      event_type_id: '2564',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'irene_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'tenant_info',
      trigger_source_nm: 'continue',
    },
    semantic: {
      semantic_event_nm: 'continue_payments_onboarding',
      topic_tag_txt: ['zrm_payments'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
    ...getZrmLeaseInfo({}),
  });

// Tax Verification Page
// https://dataportal.zillowgroup.net/event-registry/6416

const emitSubmitFromTaxVerificationPageEvent = ({ currentProperty, user }: ListingUserEventArgs) => ({
  envelope: {
    event_template_id: '168',
    event_template_version_id: '1',
    event_type_id: '6416',
    event_type_version_id: '1',
  },
  clickstream_trigger: {
    trigger_location_nm: 'irene_payments_onboarding',
    trigger_type_nm: 'interaction',
    trigger_object_nm: 'id_verify',
    trigger_source_nm: 'submit',
  },
  semantic: {
    semantic_event_nm: 'continue_payments_onboarding',
    topic_tag_txt: ['zrm_payments'],
  },
  ...getZrmListingInfo(currentProperty),
  ...getZrmUserInfo(user),
});

// https://dataportal.zillowgroup.net/event-registry/2577
const emitContinueForIndividualEvent = ({ currentProperty, user }: ListingUserEventArgs) =>
  trackListingUserEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '2577',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'irene_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'tax_information',
      trigger_source_nm: 'continue | individual',
    },
    semantic: {
      semantic_event_nm: 'continue_payments_onboarding',
      topic_tag_txt: ['zrm_payments'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/2578
const emitContinueForBusinessEvent = ({ currentProperty, user }: ListingUserEventArgs) =>
  trackListingUserEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '2578',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'irene_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'tax_information',
      trigger_source_nm: 'continue | business',
    },
    semantic: {
      semantic_event_nm: 'continue_payments_onboarding',
      topic_tag_txt: ['zrm_payments'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// Irene Account Info Page
// https://dataportal.zillowgroup.net/event-registry/2572
const emitContinueManuallyFromAccountInfoPageEvent = ({ currentProperty, user }: ListingUserEventArgs) =>
  trackLeaseListingUserEvent({
    envelope: {
      event_template_id: '177',
      event_template_version_id: '1',
      event_type_id: '2572',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'irene_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'account_info_modal',
      trigger_source_nm: 'manually_enter_account_information',
    },
    semantic: {
      semantic_event_nm: 'continue_payments_onboarding',
      topic_tag_txt: ['zrm_payments'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
    ...getZrmLeaseInfo({}),
  });

// https://dataportal.zillowgroup.net/event-registry/2573
const emitLinkToAccountManuallyFromAccountInfoPageEvent = ({ currentProperty, user }: ListingUserEventArgs) =>
  trackListingUserEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '2573',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'irene_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'account_info_modal',
      trigger_source_nm: 'link_to_this_account | manual',
    },
    semantic: {
      semantic_event_nm: 'continue_payments_onboarding',
      topic_tag_txt: ['zrm_payments'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/2574
const emitLinkThroughPlaidFromAccountInfoPageEvent = ({ currentProperty, user }: ListingUserEventArgs) =>
  trackListingUserEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '2574',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'irene_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'account_info_modal',
      trigger_source_nm: 'link_my_bank_account | plaid',
    },
    semantic: {
      semantic_event_nm: 'continue_payments_onboarding',
      topic_tag_txt: ['zrm_payments'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/2575
const emitContinueFromAccountInfoPageEvent = ({ currentProperty, user }: ListingUserEventArgs) =>
  trackListingUserEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '2575',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'irene_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'account_info_modal',
      trigger_source_nm: 'continue',
    },
    semantic: {
      semantic_event_nm: 'continue_payments_onboarding',
      topic_tag_txt: ['zrm_payments'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// Irene Confirmation Page
// https://dataportal.zillowgroup.net/event-registry/2579
const emitContinueFromConfirmationPageEvent = ({ currentProperty, user }: ListingUserEventArgs) =>
  trackListingUserEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '2579',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'irene_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'invite_tenant',
      trigger_source_nm: 'confirm_and_invite_tenant',
    },
    semantic: {
      semantic_event_nm: 'complete_payments_onboarding',
      topic_tag_txt: ['zrm_payments'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3167
const emitContinueReadyToSignCoverageRequestEvent = ({ currentProperty, user }: ListingUserEventArgs) =>
  trackListingUserEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '3167',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'irene_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'complete_payment_term_modal',
      trigger_source_nm: 'continue',
    },
    semantic: {
      semantic_event_nm: 'start_signing_coverage_request',
      topic_tag_txt: ['zrm_rental_protection'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3168
const emitContinueToSignCoverageRequestDocumentEvent = ({ currentProperty, user }: ListingUserEventArgs) =>
  trackListingUserEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '3168',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'irene_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'coverage_request_modal',
      trigger_source_nm: 'continue',
    },
    semantic: {
      semantic_event_nm: 'continue_signing_coverage_request',
      topic_tag_txt: ['zrm_rental_protection'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3169
const emitFinishSigningCoverageRequestEvent = ({ currentProperty, user }: ListingUserEventArgs) =>
  trackListingUserEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '3169',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'irene_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'coverage_request_z_docs',
      trigger_source_nm: 'sign',
    },
    semantic: {
      semantic_event_nm: 'complete_signing_coverage_request',
      topic_tag_txt: ['zrm_rental_protection'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/7463
const emitContinueFromTaxVerificationPageAsSingleLLCEvent = ({ currentProperty, user }: ListingUserEventArgs) =>
  trackListingUserEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '7463',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'irene_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'tax_information',
      trigger_source_nm: 'continue_single',
    },
    semantic: {
      semantic_event_nm: 'continue_payments_onboarding',
      topic_tag_txt: ['zrm_payments'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/7464
const emitContinueFromTaxVerificationPageAsMultiLLCEvent = ({ currentProperty, user }: ListingUserEventArgs) =>
  trackListingUserEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '7464',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'irene_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'tax_information',
      trigger_source_nm: 'continue_multi',
    },
    semantic: {
      semantic_event_nm: 'continue_payments_onboarding',
      topic_tag_txt: ['zrm_payments'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/7465
const emitContinueFromPhoneVerificationPageUsingTextEvent = ({ currentProperty, user }: ListingUserEventArgs) =>
  trackListingUserEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '7465',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'irene_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'phone_verification',
      trigger_source_nm: 'continue_text',
    },
    semantic: {
      semantic_event_nm: 'continue_payments_onboarding',
      topic_tag_txt: ['zrm_payments'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/7466
const emitContinueFromPhoneVerificationPageUsingCallEvent = ({ currentProperty, user }: ListingUserEventArgs) =>
  trackListingUserEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '7466',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'irene_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'phone_verification',
      trigger_source_nm: 'continue_call',
    },
    semantic: {
      semantic_event_nm: 'continue_payments_onboarding',
      topic_tag_txt: ['zrm_payments'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/7467
const emitContinueFromPhoneVerificationPageEvent = ({ currentProperty, user }: ListingUserEventArgs) =>
  trackListingUserEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '7467',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'irene_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'phone_verification',
      trigger_source_nm: 'continue',
    },
    semantic: {
      semantic_event_nm: 'continue_payments_onboarding',
      topic_tag_txt: ['zrm_payments'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/7469
const emitExitFromOnboardingFlowEvent = ({
  currentProperty,
  user,
  triggerObject,
}: ListingUserEventArgs & { triggerObject: string }) =>
  trackListingUserEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '7469',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'irene_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: triggerObject,
      trigger_source_nm: 'button_x',
    },
    semantic: {
      semantic_event_nm: 'exit_payments_onboarding',
      topic_tag_txt: ['zrm_payments'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-rxegistry/7468
const emitBackFromOnboardingFlowEvent = ({
  currentProperty,
  user,
  triggerObject,
}: ListingUserEventArgs & { triggerObject: string }) =>
  trackListingUserEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '7468',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'irene_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: triggerObject,
      trigger_source_nm: 'button_to_go_back',
    },
    semantic: {
      semantic_event_nm: 'back_payments_onboarding',
      topic_tag_txt: ['zrm_payments'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

const IreneOnboardingCSEvents = {
  emitStartCollectingPaymentsEvent,
  emitLearnMoreLinkEvent,
  emitContinueFromChecklistPageEvent,
  emitContinueWithLeaseSelectedEvent,
  emitContinueFromLeaseSelectorToAddPaymentManuallyEvent,
  emitContinueFromRentAmountPageEvent,
  emitContinueFromDueDatePageEvent,
  emitContinueFromTermLengthPageEvent,
  emitContinueFromSecurityDepositEvent,
  emitSkipSecurityDepositEvent,
  emitContinueFromProratedRentEvent,
  emitSkipProratedRentEvent,
  emitContinueFromOtherMICEvent,
  emitSkipOtherMICEvent,
  emitContinueFromMICDueDateEvent,
  emitContinueFromInviteTenantPageEvent,
  emitSubmitFromTaxVerificationPageEvent,
  emitContinueForIndividualEvent,
  emitContinueForBusinessEvent,
  emitContinueFromConfirmationPageEvent,
  emitContinueManuallyFromAccountInfoPageEvent,
  emitLinkToAccountManuallyFromAccountInfoPageEvent,
  emitLinkThroughPlaidFromAccountInfoPageEvent,
  emitContinueFromAccountInfoPageEvent,
  emitContinueReadyToSignCoverageRequestEvent,
  emitContinueToSignCoverageRequestDocumentEvent,
  emitFinishSigningCoverageRequestEvent,
  emitContinueFromTaxVerificationPageAsSingleLLCEvent,
  emitContinueFromTaxVerificationPageAsMultiLLCEvent,
  emitContinueFromPhoneVerificationPageUsingTextEvent,
  emitContinueFromPhoneVerificationPageUsingCallEvent,
  emitContinueFromPhoneVerificationPageEvent,
  emitBackFromOnboardingFlowEvent,
  emitExitFromOnboardingFlowEvent,
};

export default IreneOnboardingCSEvents;
